import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { CurrencyDollar, ShieldWarning, CheckCircle, Warning, DownloadSimple, BellRinging, MagnifyingGlass, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Header, Modal } from "./shared";
import PlayerDetailsModal from "./PlayerDetailsModal";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const StatCard = ({ label, value, color, testid, eye }) => (
  <div className="border border-white/10 bg-[#121212] p-5 relative" data-testid={testid}>
    {eye}
    <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-2 pr-8">{label}</div>
    <div className={`font-display text-4xl tabular-nums ${color || ""}`}>{value}</div>
  </div>
);

export default function ComplianceTab() {
  const { settings, reloadSettings } = useSettings();
  const DIVISIONS = settings.divisions || [];
  const showPaid = !!settings.show_payment_collected;
  const showBalance = !!settings.show_balance;
  const [savingVis, setSavingVis] = useState(false);
  const [maskTotals, setMaskTotals] = useState(() => {
    try { return JSON.parse(localStorage.getItem("compliance_mask_totals") || "{}"); } catch { return {}; }
  });
  const toggleMask = (k) => setMaskTotals((m) => {
    const next = { ...m, [k]: !m[k] };
    try { localStorage.setItem("compliance_mask_totals", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  const saveVisibility = async (patch) => {
    setSavingVis(true);
    try {
      await api.put("/settings", patch);
      await reloadSettings();
      const [k, v] = Object.entries(patch)[0];
      const label = k === "show_payment_collected" ? "Payment Collected" : "Balance";
      toast.success(`${label} is now ${v ? "VISIBLE to" : "HIDDEN from"} players`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingVis(false); }
  };
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [division, setDivision] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [waiverFilter, setWaiverFilter] = useState("all");
  const [reminderDays, setReminderDays] = useState(7);
  const [active, setActive] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState("set");
  const [bulkAmount, setBulkAmount] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/players").then((r) => setPlayers(r.data)).catch(() => {});
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
    api.get("/games").then((r) => setGames(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const teamById = (id) => teams.find((t) => t.id === id);
  const teamName = (id) => teamById(id)?.name || "Free Agent";
  const divisionOf = (id) => teamById(id)?.division || "Open";

  const filtered = useMemo(() => players.filter((p) => {
    if (division !== "all" && divisionOf(p.team_id) !== division) return false;
    if (teamFilter !== "all" && p.team_id !== teamFilter) return false;
    if (waiverFilter === "signed" && p.waiver_status !== "signed") return false;
    if (waiverFilter === "pending" && p.waiver_status !== "pending") return false;
    if (waiverFilter === "expired" && p.waiver_status !== "expired") return false;
    const q = search.trim().toLowerCase();
    if (q && !(`${p.name || ""}`.toLowerCase().includes(q) || `${teamName(p.team_id)}`.toLowerCase().includes(q))) return false;
    return true;
  }), [players, division, teamFilter, teams, search, waiverFilter]); // eslint-disable-line

  const seasonProgress = useMemo(() => {
    const total = players.length;
    const signed = players.filter((p) => p.waiver_status === "signed").length;
    const expired = players.filter((p) => p.waiver_status === "expired").length;
    const pct = total ? Math.round((signed / total) * 100) : 0;
    return { total, signed, expired, pct };
  }, [players]);

  const totals = useMemo(() => {    let collected = 0, due = 0, unsigned = 0, outstanding = 0;
    filtered.forEach((p) => {
      collected += p.amount_paid || 0;
      if (p.balance > 0) { due += p.balance; outstanding += 1; }
      if (!p.waiver_signed) unsigned += 1;
    });
    return { collected, due, unsigned, outstanding };
  }, [filtered]);

  const visibleTeams = division === "all" ? teams : teams.filter((t) => (t.division || "Open") === division);

  // Game-day reminders: not-eligible players whose team plays within `reminderDays`
  const reminders = useMemo(() => {
    const now = new Date();
    const cutoff = new Date(now.getTime() + reminderDays * 86400000);
    const nextGameByTeam = {};
    games.forEach((g) => {
      if ((g.status || "scheduled") !== "scheduled") return;
      const d = new Date(g.date);
      if (isNaN(d) || d < now || d > cutoff) return;
      [g.home_team_id, g.away_team_id].forEach((tid, i) => {
        if (!tid) return;
        const opp = i === 0 ? g.away_team_id : g.home_team_id;
        if (!nextGameByTeam[tid] || d < nextGameByTeam[tid].date) {
          nextGameByTeam[tid] = { date: d, opponent: opp };
        }
      });
    });
    return filtered
      .filter((p) => !p.eligible && p.team_id && nextGameByTeam[p.team_id])
      .map((p) => ({ ...p, nextGame: nextGameByTeam[p.team_id] }))
      .sort((a, b) => a.nextGame.date - b.nextGame.date);
  }, [filtered, games, reminderDays]);

  const scopeLabel = division === "all" && teamFilter === "all" && !search.trim()
    ? "all players"
    : "the current filter";

  const applyBulkFee = async () => {
    let fee_total;
    if (bulkMode === "set") {
      fee_total = parseFloat(bulkAmount);
      if (isNaN(fee_total) || fee_total < 0) { toast.error("Enter a valid fee amount"); return; }
    } else if (bulkMode === "waive") {
      fee_total = 0;
    } else {
      fee_total = null; // reset to league default
    }
    setBulkBusy(true);
    try {
      const ids = filtered.map((p) => p.id);
      const res = await api.post("/players/bulk-fee", { player_ids: ids, fee_total });
      toast.success(`Updated ${res.data.updated} player(s)`);
      setBulkAmount("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const applyBulkAction = async (action, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    setBulkBusy(true);
    try {
      const ids = filtered.map((p) => p.id);
      const res = await api.post("/players/bulk-action", { player_ids: ids, action });
      const skip = res.data.skipped ? ` (${res.data.skipped} skipped — unpaid)` : "";
      toast.success(`Updated ${res.data.updated} player(s)${skip}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const exportCsv = () => {
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ["Player", "Jersey #", "Team", "Division", "League Fee", "Amount Paid", "Balance", "Payment Status", "Waiver Signed", "Waiver Date", "Eligible", "Reason"];
    const rows = filtered.map((p) => [
      p.name, p.number != null ? p.number : "", teamName(p.team_id), divisionOf(p.team_id),
      (p.effective_fee || 0).toFixed(2), (p.amount_paid || 0).toFixed(2), (p.balance || 0).toFixed(2),
      p.payment_status, p.waiver_signed ? "Yes" : "No",
      p.waiver_signed && p.waiver_signed_at ? new Date(p.waiver_signed_at).toLocaleString() : "",
      p.eligible ? "Yes" : "No", p.eligible ? "" : (p.eligibility_reasons || []).join("; "),
    ]);
    const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const scope = division === "all" ? "all-divisions" : division.replace(/\s+/g, "-").toLowerCase();
    a.href = url;
    a.download = `eligibility-report-${scope}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="compliance-tab">
      <Header title="Compliance" />
      <p className="text-white/40 text-sm mb-6">Track waivers and league-fee payments. A player is eligible to play only when their waiver is signed and their fee is paid in full.</p>

      {/* Season waiver progress */}
      <div className="border border-white/10 bg-[#121212] p-5 mb-6" data-testid="waiver-progress">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
          <span className="text-xs uppercase tracking-[0.2em] text-white/50 font-semibold">Season Waiver Progress</span>
          <span className="text-sm font-bold" data-testid="waiver-progress-count"><span className="text-[#00E676]">{seasonProgress.signed}</span> <span className="text-white/40">of</span> {seasonProgress.total} players signed · {seasonProgress.pct}%{seasonProgress.expired > 0 && <span className="text-[#FF9500] ml-2" data-testid="waiver-progress-expired">· {seasonProgress.expired} expired</span>}</span>
        </div>
        <div className="h-2.5 bg-white/10 overflow-hidden">
          <div className="h-full bg-[#00E676] transition-all duration-500" style={{ width: `${seasonProgress.pct}%` }} data-testid="waiver-progress-bar" />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search player or team…" data-testid="compliance-search"
            className="w-full bg-[#1A1A1A] border border-white/15 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all"
          />
        </div>
        <select value={division} onChange={(e) => { setDivision(e.target.value); setTeamFilter("all"); }} data-testid="compliance-division-filter" className="bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm">
          <option value="all">All Divisions</option>
          {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} data-testid="compliance-team-filter" className="bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm">
          <option value="all">All Teams</option>
          {visibleTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={waiverFilter} onChange={(e) => setWaiverFilter(e.target.value)} data-testid="compliance-waiver-filter" className="bg-[#1A1A1A] border border-white/15 px-3 py-2 text-sm">
          <option value="all">All Waivers</option>
          <option value="signed">Waiver: Signed</option>
          <option value="pending">Waiver: Pending</option>
          <option value="expired">Waiver: Expired</option>
        </select>
        <button onClick={() => setBulkOpen(true)} disabled={filtered.length === 0} data-testid="bulk-fee-btn" className="ml-auto flex items-center gap-2 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] disabled:opacity-40 text-white/70 text-sm font-bold uppercase tracking-wide px-4 py-2 transition-all duration-200">
          <CurrencyDollar size={16} /> Bulk Actions
        </button>
        <button onClick={exportCsv} disabled={filtered.length === 0} data-testid="export-eligibility-csv" className="flex items-center gap-2 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] disabled:opacity-40 text-white/70 text-sm font-bold uppercase tracking-wide px-4 py-2 transition-all duration-200">
          <DownloadSimple size={16} /> Export CSV
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Payments Collected" value={maskTotals.collected ? "••••••" : money(totals.collected)} color="text-[#00E676]" testid="total-collected"
          eye={<button onClick={() => toggleMask("collected")} data-testid="stat-eye-paid" title={maskTotals.collected ? "Hidden — click to show total" : "Visible — click to hide total"} className="absolute top-4 right-4 hover:scale-110 transition-transform">{maskTotals.collected ? <EyeSlash size={18} className="text-white/40" /> : <Eye size={18} weight="fill" className="text-[#00E676]" />}</button>} />
        <StatCard label="Balances Due" value={maskTotals.due ? "••••••" : money(totals.due)} color="text-[#FF3B30]" testid="total-due"
          eye={<button onClick={() => toggleMask("due")} data-testid="stat-eye-balance" title={maskTotals.due ? "Hidden — click to show total" : "Visible — click to hide total"} className="absolute top-4 right-4 hover:scale-110 transition-transform">{maskTotals.due ? <EyeSlash size={18} className="text-white/40" /> : <Eye size={18} weight="fill" className="text-[#00E676]" />}</button>} />
        <StatCard label="Unsigned Waivers" value={totals.unsigned} color="text-[#FFEA00]" testid="total-unsigned" />
        <StatCard label="Outstanding Balances" value={totals.outstanding} color="text-[#FF9800]" testid="total-outstanding" />
      </div>

      {/* Game-day payment/waiver reminders */}
      <div className="border border-[#FF9800]/30 bg-[#FF9800]/5 mb-8" data-testid="reminders-panel">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <BellRinging size={20} className="text-[#FF9800]" weight="fill" />
            <h3 className="font-display text-xl uppercase">Game-Day Reminders</h3>
            <span className="text-white/40 text-xs">— not-eligible players with a game coming up</span>
          </div>
          <select value={reminderDays} onChange={(e) => setReminderDays(Number(e.target.value))} data-testid="reminder-days-select" className="bg-[#1A1A1A] border border-white/15 px-3 py-1.5 text-sm">
            <option value={7}>Next 7 days</option>
            <option value={14}>Next 14 days</option>
            <option value={30}>Next 30 days</option>
          </select>
        </div>
        {reminders.length === 0 ? (
          <p className="px-5 py-6 text-white/40 text-sm">No players need chasing — everyone with an upcoming game is eligible. 🎉</p>
        ) : (
          <div className="divide-y divide-white/5">
            {reminders.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-5 py-3 flex-wrap gap-2" data-testid="reminder-row">
                <div className="min-w-0">
                  <div className="font-semibold text-sm"><span className="uppercase">{p.number != null ? <span className="text-[#FF5722]">#{p.number} </span> : ""}{p.name}</span> <span className="text-white/40 font-normal">· {teamName(p.team_id)}</span></div>
                  <div className="text-xs text-white/50 mt-0.5">
                    {p.balance > 0 && <span className="text-[#FF3B30] font-semibold">Owes {money(p.balance)}</span>}
                    {p.balance > 0 && !p.waiver_signed && <span className="text-white/30"> · </span>}
                    {!p.waiver_signed && <span className="text-[#FFEA00] font-semibold">Waiver pending</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wide text-white/40">Plays vs {teamName(p.nextGame.opponent)}</div>
                    <div className="text-sm font-semibold text-[#FF9800]">{p.nextGame.date.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}</div>
                  </div>
                  <button onClick={() => setActive(p)} data-testid={`reminder-manage-${p.id}`} className="text-xs uppercase font-bold px-3 py-1.5 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 transition-colors">Resolve</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="border border-white/10 bg-[#121212] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-white/50 uppercase text-xs tracking-wider">
              <th className="text-left py-3 px-4">Player</th>
              <th className="text-left py-3 px-4">Team</th>
              <th className="text-right py-3 px-3">Fee</th>
              <th className="text-right py-3 px-3">
                <button onClick={() => saveVisibility({ show_payment_collected: !showPaid })} disabled={savingVis} data-testid="header-toggle-paid" title={showPaid ? "Visible to players — click to hide" : "Hidden from players — click to show"} className="inline-flex items-center gap-1.5 hover:text-[#FF5722] transition-colors">
                  Paid {showPaid ? <Eye size={13} weight="fill" className="text-[#00E676]" /> : <EyeSlash size={13} className="text-white/40" />}
                </button>
              </th>
              <th className="text-right py-3 px-3">
                <button onClick={() => saveVisibility({ show_balance: !showBalance })} disabled={savingVis} data-testid="header-toggle-balance" title={showBalance ? "Visible to players — click to hide" : "Hidden from players — click to show"} className="inline-flex items-center gap-1.5 hover:text-[#FF5722] transition-colors">
                  Balance {showBalance ? <Eye size={13} weight="fill" className="text-[#00E676]" /> : <EyeSlash size={13} className="text-white/40" />}
                </button>
              </th>
              <th className="text-center py-3 px-3">Waiver</th>
              <th className="text-center py-3 px-3">Eligibility</th>
              <th className="text-right py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-white/5 last:border-0 hover:bg-white/5" data-testid="compliance-row">
                <td className="py-3 px-4 font-semibold uppercase">{p.number != null ? <span className="text-[#FF5722]">#{p.number} </span> : ""}{p.name}</td>
                <td className="py-3 px-4 text-white/50">{teamName(p.team_id)}</td>
                <td className="py-3 px-3 text-right tabular-nums text-white/70">{money(p.effective_fee)}</td>
                <td className="py-3 px-3 text-right tabular-nums text-[#00E676]">{money(p.amount_paid)}</td>
                <td className={`py-3 px-3 text-right tabular-nums ${p.balance > 0 ? "text-[#FF3B30]" : "text-white/50"}`}>{money(p.balance)}</td>
                <td className="py-3 px-3 text-center">
                  {p.waiver_status === "signed"
                    ? <CheckCircle size={18} weight="fill" className="text-[#00E676] inline" />
                    : p.waiver_status === "expired"
                    ? <span className="text-[10px] uppercase font-bold bg-[#FF9500]/20 text-[#FF9500] px-2 py-0.5" title="Signed an older waiver version — must re-sign">Expired</span>
                    : <span className="text-[10px] uppercase font-bold bg-[#FFEA00]/20 text-[#FFEA00] px-2 py-0.5">Pending</span>}
                </td>
                <td className="py-3 px-3 text-center">
                  {p.eligible
                    ? <span className="text-[10px] uppercase font-bold bg-[#00E676]/20 text-[#00E676] px-2 py-0.5">Eligible</span>
                    : <span className="text-[10px] uppercase font-bold bg-[#FF3B30]/20 text-[#FF3B30] px-2 py-0.5" title={p.eligibility_reasons.join(" · ")}>Not Eligible</span>}
                </td>
                <td className="py-3 px-4 text-right">
                  <button onClick={() => setActive(p)} data-testid={`manage-compliance-${p.id}`} className="inline-flex items-center gap-1 text-xs uppercase font-bold px-3 py-1.5 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 transition-colors">
                    <CurrencyDollar size={14} /> Manage
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-white/40">No players match these filters.</td></tr>}
          </tbody>
        </table>
      </div>

      {active && (
        <PlayerDetailsModal
          player={active}
          onClose={() => setActive(null)}
          onSaved={(updated) => { setActive(updated); setPlayers((prev) => prev.map((x) => (x.id === updated.id ? updated : x))); }}
        />
      )}

      {bulkOpen && (
        <Modal open onClose={() => setBulkOpen(false)} title="Bulk Actions">
          <div data-testid="bulk-fee-modal" className="space-y-5">
            <p className="text-sm text-white/60">Applies to <span className="text-[#FF5722] font-bold" data-testid="bulk-fee-count">{filtered.length} player(s)</span> in {scopeLabel}. Adjust the Division/Team/search filters first to change who's affected.</p>

            <div className="border border-white/10 p-4">
              <div className="text-[10px] uppercase tracking-wide text-[#FF5722] font-bold mb-3">Set Fee</div>
              <div className="space-y-2">
                {[
                  { k: "set", label: "Set fee to a specific amount" },
                  { k: "waive", label: "Waive fee (set to $0 — counts as paid)" },
                  { k: "reset", label: "Reset to league default" },
                ].map((o) => (
                  <label key={o.k} className={`flex items-center gap-3 border px-4 py-2.5 cursor-pointer transition-colors ${bulkMode === o.k ? "border-[#FF5722] bg-[#FF5722]/5" : "border-white/10 hover:border-white/30"}`}>
                    <input type="radio" name="bulkmode" checked={bulkMode === o.k} onChange={() => setBulkMode(o.k)} data-testid={`bulk-mode-${o.k}`} className="accent-[#FF5722]" />
                    <span className="text-sm">{o.label}</span>
                  </label>
                ))}
              </div>
              {bulkMode === "set" && (
                <div className="flex items-center gap-2 max-w-[180px] mt-3">
                  <span className="text-white/50 font-display text-xl">$</span>
                  <input type="number" min={0} value={bulkAmount} onChange={(e) => setBulkAmount(e.target.value)} placeholder="120" data-testid="bulk-fee-amount" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722]" />
                </div>
              )}
              <button onClick={applyBulkFee} disabled={bulkBusy} data-testid="bulk-fee-apply" className="w-full mt-3 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-2.5 transition-all duration-200">Apply Fee to {filtered.length}</button>
            </div>

            <div className="border border-white/10 p-4">
              <div className="text-[10px] uppercase tracking-wide text-[#00E676] font-bold mb-3">Payment</div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => applyBulkAction("mark_paid", `Mark all ${filtered.length} player(s) as paid in full?`)} disabled={bulkBusy} data-testid="bulk-mark-paid" className="border border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10 disabled:opacity-40 text-xs uppercase font-bold py-2.5 transition-colors">Mark All Paid</button>
                <button onClick={() => applyBulkAction("mark_unpaid", `Reset all ${filtered.length} player(s) to unpaid?`)} disabled={bulkBusy} data-testid="bulk-mark-unpaid" className="border border-white/20 text-white/60 hover:bg-white/5 disabled:opacity-40 text-xs uppercase font-bold py-2.5 transition-colors">Mark All Unpaid</button>
              </div>
            </div>

            <div className="border border-white/10 p-4">
              <div className="text-[10px] uppercase tracking-wide text-[#FF5722] font-bold mb-1">Waiver</div>
              <p className="text-white/40 text-[11px] mb-3">Marking signed skips anyone not paid in full.</p>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => applyBulkAction("waiver_sign", `Mark waiver signed for all eligible (paid) players in this filter?`)} disabled={bulkBusy} data-testid="bulk-waiver-sign" className="border border-[#FF5722]/50 text-[#FF5722] hover:bg-[#FF5722]/10 disabled:opacity-40 text-xs uppercase font-bold py-2.5 transition-colors">Mark Waiver Signed</button>
                <button onClick={() => applyBulkAction("waiver_revoke", `Revoke waivers for all ${filtered.length} player(s)? This affects all their teams.`)} disabled={bulkBusy} data-testid="bulk-waiver-revoke" className="border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-40 text-xs uppercase font-bold py-2.5 transition-colors">Revoke Waivers</button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
